import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAiCommandPalette } from '../context/AiCommandPaletteContext';
import StatusBotIcon from '../../StatusBotIcon';
import { useAuth } from '../../../context/AuthContext';
import { useAiPageContext } from '../../../context/AiPageContextProvider';
import { STATUS_COLORS } from '../../../lib/colorsMap';
import { getIconComponent } from '../../../lib/statusIconsMap';
import { PiUserCircleDashed } from 'react-icons/pi';

// Premium-styled task card component for AI results - 50% width for 2-column layout
const TaskLinkCard = ({ href, taskKey, summary, status, statusColor, statusIcon, assignee, assigneeColor, onClick }) => {
  // Get status icon component
  const StatusIconComponent = statusIcon ? getIconComponent(statusIcon) : null;
  
  // Get status color from STATUS_COLORS map
  const statusColorClass = statusColor && STATUS_COLORS[statusColor]?.text 
    ? STATUS_COLORS[statusColor].text 
    : 'text-neutral-500';
  
  // Get assignee dot color - assigneeColor is already a bg-* class
  const assigneeDotClass = assigneeColor || 'bg-neutral-500';

  return (
    <div 
      onClick={onClick}
      className="cursor-pointer group block bg-gradient-to-r from-purple-50/40 to-violet-50/30 dark:from-purple-500/5 dark:to-violet-500/[0.02] border border-purple-200/30 dark:border-purple-400/10 rounded-md px-2.5 py-1.5 hover:from-purple-100/50 hover:to-violet-100/40 dark:hover:from-purple-400/15 dark:hover:to-violet-400/5 hover:border-purple-300/40 dark:hover:border-purple-400/18 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md backdrop-blur-sm"
    >
      <div className="flex flex-col min-w-0">
        <span className="font-medium text-neutral-900 dark:text-white text-xs truncate group-hover:text-neutral-700 dark:group-hover:text-neutral-100 transition-colors">{summary}</span>
        <div className="flex items-center mt-0.5 gap-2 min-w-0">
          <span className="text-[10px] text-neutral-900/55 dark:text-white/55 flex-shrink-0">{taskKey}</span>
          <div className="flex items-center gap-1 min-w-0">
            {StatusIconComponent ? (
              <StatusIconComponent className={`w-3 h-3 flex-shrink-0 ${statusColorClass}`} />
            ) : (
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 bg-neutral-500`}></span>
            )}
            <span className="text-[10px] text-neutral-900/55 dark:text-white/55 truncate">{status}</span>
          </div>
          <div className="flex items-center gap-1 min-w-0">
            {assignee === 'Unassigned' ? (
              <PiUserCircleDashed className="w-3 h-3 flex-shrink-0 text-neutral-900/40 dark:text-white/40" />
            ) : (
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${assigneeDotClass} text-white text-[5px] font-bold flex items-center justify-center leading-none`}>
                {assignee.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="text-[10px] text-neutral-900/55 dark:text-white/55 truncate">{assignee}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple inline task link component (summary optional)
const TaskLink = ({ href, taskKey, summary, status, assignee, onClick }) => {
  return (
    <a 
      onClick={onClick}
      href={href}
      className="cursor-pointer group inline-flex items-center bg-teal-100/80 dark:bg-teal-800/30 hover:bg-teal-200/80 dark:hover:bg-teal-700/40 rounded px-1.5 py-0.5 text-xs border border-teal-200/70 dark:border-teal-600/40 hover:border-teal-300 dark:hover:border-teal-500/50 transition-all duration-200 mx-0.5 shadow-sm hover:shadow hover:scale-[1.01] no-underline"
    >
      <span className="font-semibold text-teal-600 dark:text-teal-300 group-hover:text-teal-700 dark:group-hover:text-teal-200 transition-colors duration-200">{taskKey}</span>
      {summary ? (
        <>
          <span className="mx-1 text-teal-400/70 dark:text-teal-500/70">•</span>
          <span className="truncate max-w-[290px] text-teal-700/90 dark:text-teal-100 group-hover:text-teal-800 dark:group-hover:text-teal-50 transition-colors duration-200">{summary}</span>
        </>
      ) : null}
    </a>
  );
};

// Monitor status card component for AI results
const MonitorStatusCard = ({ monitor, onClick }) => {
  const isUp = monitor.status === 'up';
  const isActive = monitor.isActive;
  
  // Determine status dot color based on monitor state
  const statusDotColor = !isActive
    ? 'text-amber-500'
    : isUp
      ? 'text-green-500'
      : 'text-red-500';
  
  // Format actionType (replace underscores with spaces and capitalize)
  const formatActionType = (type) => {
    if (!type) return '';
    const formatted = type.replace('_', ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };
  
  // Format response time (milliseconds to appropriate unit)
  const formatResponseTime = (ms) => {
    if (!ms && ms !== 0) return '';
    
    // Less than 1 second - show milliseconds
    if (ms < 1000) {
      return `${Math.round(ms)}ms`;
    }
    
    // Less than 60 seconds - show seconds with 1 decimal
    if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    
    // 60 seconds or more - show minutes and seconds
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.round((ms % 60000) / 1000);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  };
  
  // Simplify URL for display (remove protocol and www, truncate if needed)
  const simplifyUrl = (url) => {
    if (!url) return '';
    try {
      let simplified = url
        .replace(/^https?:\/\//, '')  // Remove protocol
        .replace(/^www\./, '');        // Remove www
      
      // If still too long, truncate
      const maxLength = 30;
      if (simplified.length > maxLength) {
        simplified = simplified.substring(0, maxLength) + '...';
      }
      
      return simplified;
    } catch {
      return url;
    }
  };
  
  return (
    <div 
      onClick={onClick}
      className="cursor-pointer group block bg-gradient-to-r from-purple-50/40 to-violet-50/30 dark:from-purple-500/5 dark:to-violet-500/[0.02] border border-purple-200/30 dark:border-purple-400/10 rounded-md px-2.5 py-1.5 hover:from-purple-100/50 hover:to-violet-100/40 dark:hover:from-purple-400/15 dark:hover:to-violet-400/5 hover:border-purple-300/40 dark:hover:border-purple-400/18 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md backdrop-blur-sm"
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex flex-col min-w-0 flex-1">
          <span className="font-medium text-neutral-900 dark:text-white text-xs truncate group-hover:text-neutral-700 dark:group-hover:text-neutral-100 transition-colors">
            {monitor.name}
          </span>
          <div className="flex items-center mt-0.5 gap-2 min-w-0">
            <span className="text-[10px] text-neutral-900/55 dark:text-white/55 flex-shrink-0">
              {formatActionType(monitor.actionType)}
            </span>
            {monitor.responseTime !== undefined && (
              <span className="text-[10px] text-neutral-900/55 dark:text-white/55 flex-shrink-0">
                {formatResponseTime(monitor.responseTime)}
              </span>
            )}
            {monitor.url && (
              <span className="text-[10px] text-neutral-900/55 dark:text-white/55 truncate">
                {simplifyUrl(monitor.url)}
              </span>
            )}
          </div>
        </div>
        <span className={`text-xs flex-shrink-0 ${statusDotColor}`}>
          ●
        </span>
      </div>
    </div>
  );
};

// Minimal empty state component for blocks with no data
const EmptyBlockState = ({ type }) => {
  const getMessage = () => {
    switch (type) {
      case 'task_list':
        return 'No matching tasks found';
      case 'monitor_status':
        return 'No matching monitors found';
      default:
        return 'No results found';
    }
  };

  return (
    <div className="my-3 flex items-center justify-center px-3 py-2.5 rounded-md bg-gradient-to-r from-purple-100/40 to-violet-100/30 dark:from-purple-500/[0.08] dark:to-violet-500/[0.04] border border-purple-200/30 dark:border-purple-400/10 backdrop-blur-sm">
      <span className="text-xs text-purple-400/70 dark:text-purple-300/50">
        {getMessage()}
      </span>
    </div>
  );
};

// Simple, clean Markdown formatter with ReactMarkdown and generic placeholder support
const MarkdownFormatter = ({ markdown, registries = {}, isStreaming = false }) => {
  if (!markdown) return null;
  
  const { handleLinkClick, handleNavigateToTask, handleNavigateToMonitor } = useAiCommandPalette();
  
  // Parse markdown for generic {{TYPE:ID}} placeholders
  const parts = markdown.split(/({{[A-Z_]+:\d+}})/g);
  
  // If no placeholders, render normally
  if (parts.length === 1) {
    return <MarkdownContent markdown={markdown} handleLinkClick={handleLinkClick} />;
  }
  
  // Render with generic block injections
  return (
    <div className="prose dark:prose-invert">
      {parts.map((part, index) => {
        // Check if this part is a generic placeholder {{TYPE:ID}}
        const match = part.match(/{{([A-Z_]+):(\d+)}}/);
        
        if (match) {
          const [, blockType, blockId] = match;
          const typeKey = blockType.toLowerCase();
          const blockData = registries[typeKey]?.[parseInt(blockId)];
          
          // Render based on block type
          if (typeKey === 'task_list' && blockData?.tasks?.length > 0) {
            return (
              <div 
                key={`${typeKey}-${index}`} 
                className="my-4 grid grid-cols-2 gap-2 not-prose"
              >
                {blockData.tasks.map((task) => (
                  <div key={task.id}>
                    <TaskLinkCard
                      href={`/${task.organization.slug}/plan/${task.key}`}
                      taskKey={task.key}
                      summary={task.summary}
                      status={task.statusConfig?.name || task.status}
                      statusColor={task.statusConfig?.color || ''}
                      statusIcon={task.statusConfig?.icon || ''}
                      assignee={task.assignee?.name || 'Unassigned'}
                      assigneeColor={task.assignee?.avatarColor || ''}
                      onClick={() => handleNavigateToTask(task)}
                    />
                  </div>
                ))}
              </div>
            );
          }
          
          if (typeKey === 'monitor_status' && blockData?.monitors?.length > 0) {
            return (
              <div 
                key={`${typeKey}-${index}`} 
                className="my-4 grid grid-cols-2 gap-2 not-prose"
              >
                {blockData.monitors.map((monitor) => (
                  <div key={monitor.id}>
                    <MonitorStatusCard
                      monitor={monitor}
                      onClick={() => handleNavigateToMonitor(monitor)}
                    />
                  </div>
                ))}
              </div>
            );
          }
          
          // Block data exists but is empty (confirmed no results)
          if (blockData !== undefined) {
            return <EmptyBlockState key={`${typeKey}-${index}`} type={typeKey} />;
          }
          
          // Still streaming and no data yet - show brief skeleton
          if (isStreaming) {
            return (
              <div 
                key={`${typeKey}-${index}`} 
                className="my-4 grid grid-cols-2 gap-2 not-prose"
              >
                {[1, 2].map(i => (
                  <div key={i} className="h-12 bg-neutral-100 dark:bg-neutral-800/40 rounded-md animate-pulse" />
                ))}
              </div>
            );
          }
          
          // Not streaming and no data - show empty state
          return <EmptyBlockState key={`${typeKey}-${index}`} type={typeKey} />;
        }
        
        // Regular markdown content
        return <MarkdownContent key={`text-${index}`} markdown={part} handleLinkClick={handleLinkClick} />;
      })}
    </div>
  );
};

// Extracted markdown rendering component
const MarkdownContent = ({ markdown, handleLinkClick }) => {
  if (!markdown || !markdown.trim()) return null;
  
  return (
    <div className="prose dark:prose-invert">
      <ReactMarkdown
        components={{
          a: ({ node, href, children }) => {
            // Check if this is a task link. Support:
            // - /org/plan/KEY
            // - org/plan/KEY
            // - https://domain.tld/org/plan/KEY
            const hrefMatchGeneral = href && href.match(/(?:https?:\/\/[^/]+)?\/?([^/]+)\/plan\/([A-Z]+-\d+)/i);
            const isTaskLink = Boolean(hrefMatchGeneral);

            if (isTaskLink) {
              // Parse children to extract the text content
              // ReactMarkdown might pass children as strings, arrays, or React elements
              let childrenText = '';
              
              // Try to extract text from various children formats
              if (typeof children === 'string') {
                childrenText = children;
              } else if (Array.isArray(children)) {
                // Flatten and join all children text content
                childrenText = children
                  .map(child => {
                    if (typeof child === 'string') return child;
                    if (child?.props?.children) {
                      if (typeof child.props.children === 'string') return child.props.children;
                      if (Array.isArray(child.props.children)) {
                        return child.props.children.map(c => 
                          typeof c === 'string' ? c : ''
                        ).join('');
                      }
                    }
                    return '';
                  })
                  .join('');
              } else if (children?.props?.children) {
                if (typeof children.props.children === 'string') {
                  childrenText = children.props.children;
                }
              }
              
              // If we have text content, try to parse the parts (be tolerant of missing fields)
              if (childrenText) {
                // Extract org and taskKey from href as authoritative values (supports multiple formats)
                const hrefMatch = href.match(/(?:https?:\/\/[^/]+)?\/?([^/]+)\/plan\/([A-Z]+-\d+)/i);
                const hrefOrgSlug = hrefMatch ? hrefMatch[1] : null;
                const hrefTaskKey = hrefMatch ? hrefMatch[2] : null;

                // Split on middle dot with optional spaces, filter empty segments
                const rawParts = childrenText.split(/\s*·\s*/).map(p => (p || '').trim());
                const parts = rawParts.filter(p => p.length > 0);

                // Determine fields with graceful fallbacks
                let taskKey = parts[0] || hrefTaskKey || '';
                let summary = parts[1] || '';
                let status = parts[2] || '';
                let assignee = parts[3] || '';

                // If the first token isn't a key shape but href has it, shift
                if (!/^([A-Z]+-\d+)$/i.test(taskKey) && hrefTaskKey) {
                  // Rebuild summary including first token
                  summary = [taskKey, summary].filter(Boolean).join(' ').trim();
                  taskKey = hrefTaskKey;
                }

                // If we still don't have a summary, use the remaining joined text
                if (!summary && parts.length > 1) {
                  summary = parts.slice(1).join(' · ');
                }

                // Final sanity checks
                taskKey = taskKey.trim();
                summary = summary.trim();

                if (taskKey) {
                  // Normalize href for router navigation
                  const normalizedHref = hrefOrgSlug && hrefTaskKey
                    ? `/${hrefOrgSlug}/plan/${hrefTaskKey}`
                    : href;
                  return (
                    <TaskLink
                      onClick={() => handleLinkClick(normalizedHref)}
                      href={normalizedHref}
                      taskKey={taskKey}
                      summary={summary}
                      status={status}
                      assignee={assignee}
                    />
                  );
                }
              }
            }
            
            // Default link rendering
            return (
              <button
                onClick={() => handleLinkClick(href)}
                className="bg-teal-500/10 hover:bg-teal-500/20 text-teal-500 font-medium transition-all rounded-lg px-3 py-1 text-left truncate max-w-full"
              >
                {children}
              </button>
            );
          },
          p: ({ node, children }) => (
            <p className="text-foreground mt-3.5 mb-1.5 last:mb-0">
              {children}
            </p>
          ),
          // Add styling for other markdown elements as needed
          h1: ({ node, children }) => (
            <h1 className="text-lg font-bold mt-5 mb-3 first:mt-0">{children}</h1>
          ),
          h2: ({ node, children }) => (
            <h2 className="text-lg font-bold mt-4 mb-2">{children}</h2>
          ),
          h3: ({ node, children }) => (
            <h3 className="text-md font-bold mt-3 mb-1.5">{children}</h3>
          ),
          ul: ({ node, children }) => (
            <ul className="list-disc pl-5 my-2">{children}</ul>
          ),
          ol: ({ node, children }) => (
            <ol className="list-decimal pl-5 my-2">{children}</ol>
          ),
          li: ({ node, children }) => (
            <li className="my-1">{children}</li>
          ),
          pre: ({ children }) => (
            <pre className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded my-4 overflow-x-auto text-sm font-mono">
              {children}
            </pre>
          ),
          code: ({ children }) => (
            <code className="px-1 py-0.5 bg-violet-100 dark:bg-violet-900/30 rounded text-sm font-mono font-normal before:content-none after:content-none">
              {children}
            </code>
          ),
          blockquote: ({ node, children }) => (
            <blockquote className="border-l-4 border-violet-300 dark:border-violet-600 pl-4 italic my-4">{children}</blockquote>
          ),
          hr: ({ node, children }) => (
            <hr className="my-4 border-t border-violet-200 dark:border-violet-700/50" />
          ),
        }}
        remarkPlugins={[remarkGfm]}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};


// Completed message component (shows a finished Q&A pair)
const CompletedMessage = ({ userQuery, aiBlocks, registries }) => {
  const { user } = useAuth();
  const { handleNavigateToTask } = useAiCommandPalette();
  
  const getAvatarColorClass = () => {
    if (!user?.avatarColor) return 'bg-gray-500';
    return user.avatarColor.startsWith('bg-') ? user.avatarColor : `bg-${user.avatarColor}`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/50 dark:border-violet-500/20 p-4 flex flex-col gap-3"
    >
      {/* User question */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center pt-1">
          <div className={`h-5 w-5 rounded-full ${getAvatarColorClass()} flex items-center justify-center text-white text-xs font-semibold`}>
            {user.name.split(' ').map(n => n[0]).join('')}
          </div>
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="font-medium text-foreground">
            {userQuery}
          </div>
        </div>
      </div>

      {/* AI response */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center mt-4 pt-0.5">
          <StatusBotIcon className="h-5 w-5 text-teal-500" />
        </div>
        <div className="flex-1 min-w-0">
          {aiBlocks.map((block, index) => {
            if (block.type === 'text' && block.content) {
              return (
                <div key={`text-${index}`}>
                  <MarkdownFormatter 
                    markdown={block.content} 
                    registries={registries}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    </motion.div>
  );
};

// Current/streaming message component
const CurrentMessage = ({ userQuery, aiBlocks, registries, isStreaming, status, isLoading }) => {
  const { user } = useAuth();
  
  const getAvatarColorClass = () => {
    if (!user?.avatarColor) return 'bg-gray-500';
    return user.avatarColor.startsWith('bg-') ? user.avatarColor : `bg-${user.avatarColor}`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-200/50 dark:border-violet-500/20 p-4 flex flex-col gap-3"
    >
      {/* User question */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center pt-1">
          <div className={`h-5 w-5 rounded-full ${getAvatarColorClass()} flex items-center justify-center text-white text-xs font-semibold`}>
            {user.name.split(' ').map(n => n[0]).join('')}
          </div>
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="font-medium text-foreground">
            {userQuery}
          </div>
        </div>
      </div>

      {/* AI response area */}
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center mt-4 pt-0.5">
          <StatusBotIcon className="h-5 w-5 text-teal-500" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Show real-time status from AI if no content yet */}
          {status && aiBlocks.length === 0 && (
            <div className="mt-4">
              <span className="relative inline-block bg-radial from-black/40 from-40% to-black/80 to-60% dark:from-white/40 dark:from-40% dark:to-white/80 dark:to-60% bg-clip-text text-transparent animate-shimmer max-w-full truncate">
                {status}
              </span>
            </div>
          )}
          
          {/* Render blocks in sequence */}
          {aiBlocks.map((block, index) => {
            if (block.type === 'text' && block.content) {
              return (
                <div key={`text-${index}`}>
                  <MarkdownFormatter 
                    markdown={block.content} 
                    registries={registries}
                    isStreaming={isStreaming}
                  />
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    </motion.div>
  );
};

const AiResults = () => {
  const {
    isLoading,
    aiResults,
    isAskMode,
    completedMessages,
    currentMessage,
    registries
  } = useAiCommandPalette();

  if (!isAskMode && !aiResults) return null;

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        {/* Render current/streaming message at TOP */}
        {currentMessage.userQuery && (
          <CurrentMessage
            userQuery={currentMessage.userQuery}
            aiBlocks={currentMessage.aiBlocks}
            registries={registries}
            isStreaming={currentMessage.isStreaming}
            status={currentMessage.status}
            isLoading={isLoading}
          />
        )}

        {/* Render completed messages below (newest to oldest) */}
        {[...completedMessages].reverse().map((msg, index) => (
          <CompletedMessage 
            key={completedMessages.length - 1 - index}
            userQuery={msg.userQuery}
            aiBlocks={msg.aiBlocks}
            registries={msg.registries}
          />
        ))}
      </div>
    </div>
  );
};

export default AiResults; 